import { el } from '../../utils/dom';

export type FieldDef =
  | { kind: 'text'; key: string; label: string; required?: boolean; placeholder?: string }
  | { kind: 'number'; key: string; label: string; required?: boolean }
  | { kind: 'select'; key: string; label: string; options: { value: string; label: string }[] }
  | {
      kind: 'textarea';
      key: string;
      label: string;
      rows?: number;
      required?: boolean;
      placeholder?: string;
      hint?: string;
    }
  | { kind: 'checkbox'; key: string; label: string };

export type FieldValue = string | number | boolean;

export type FormApi = {
  element: HTMLElement;
  values: () => Record<string, FieldValue>;
  setValues: (vals: Record<string, FieldValue>) => void;
  reset: () => void;
};

export function buildForm(fields: FieldDef[]): FormApi {
  const root = el('div', { className: 'ev123-manage-form' });
  const refs = new Map<string, HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>();
  const initial: Record<string, FieldValue> = {};

  for (const f of fields) {
    const wrap = el('div', { className: 'ev123-manage-form__field' });
    const id = `mf_${f.key}_${Math.random().toString(36).slice(2, 8)}`;

    if (f.kind === 'checkbox') {
      const row = el('label', { className: 'ev123-manage-form__check' });
      const input = el('input', { type: 'checkbox', id }) as HTMLInputElement;
      const span = el('span', { text: f.label });
      row.append(input, span);
      wrap.appendChild(row);
      refs.set(f.key, input);
      initial[f.key] = false;
    } else {
      const labelEl = el('label', {
        className: 'ev123-manage-form__label',
        for: id,
        text: f.label + ((f as { required?: boolean }).required ? ' *' : ''),
      });
      wrap.appendChild(labelEl);

      if (f.kind === 'select') {
        const sel = el('select', { id, className: 'ev123-input' }) as HTMLSelectElement;
        for (const opt of f.options) {
          const o = document.createElement('option');
          o.value = opt.value;
          o.textContent = opt.label;
          sel.appendChild(o);
        }
        wrap.appendChild(sel);
        refs.set(f.key, sel);
        initial[f.key] = f.options[0]?.value ?? '';
      } else if (f.kind === 'textarea') {
        const ta = el('textarea', {
          id,
          className: 'ev123-input ev123-manage-form__textarea',
          rows: String(f.rows ?? 4),
          placeholder: f.placeholder ?? '',
        }) as HTMLTextAreaElement;
        wrap.appendChild(ta);
        if (f.hint) {
          wrap.appendChild(el('p', { className: 'ev123-manage-form__hint', text: f.hint }));
        }
        refs.set(f.key, ta);
        initial[f.key] = '';
      } else if (f.kind === 'number') {
        const input = el('input', {
          id,
          type: 'number',
          className: 'ev123-input',
          step: '1',
        }) as HTMLInputElement;
        wrap.appendChild(input);
        refs.set(f.key, input);
        initial[f.key] = 0;
      } else {
        const input = el('input', {
          id,
          type: 'text',
          className: 'ev123-input',
          placeholder: f.placeholder ?? '',
        }) as HTMLInputElement;
        wrap.appendChild(input);
        refs.set(f.key, input);
        initial[f.key] = '';
      }
    }

    root.appendChild(wrap);
  }

  function values(): Record<string, FieldValue> {
    const out: Record<string, FieldValue> = {};
    for (const f of fields) {
      const node = refs.get(f.key);
      if (!node) continue;
      if (f.kind === 'checkbox') out[f.key] = (node as HTMLInputElement).checked;
      else if (f.kind === 'number') {
        const n = Number((node as HTMLInputElement).value);
        out[f.key] = Number.isFinite(n) ? n : 0;
      } else out[f.key] = node.value;
    }
    return out;
  }

  function setValues(vals: Record<string, FieldValue>): void {
    for (const f of fields) {
      const node = refs.get(f.key);
      if (!node) continue;
      const v = vals[f.key];
      if (f.kind === 'checkbox') (node as HTMLInputElement).checked = v === true;
      else if (f.kind === 'number') (node as HTMLInputElement).value = String(v ?? 0);
      else (node as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value = String(v ?? '');
    }
  }

  function reset(): void {
    setValues(initial);
  }

  return { element: root, values, setValues, reset };
}
